<template>
  <div class="chat-page">
    <!-- 헤더 -->
    <AppHeader title="꾸우챗" :showBack="true">
      <template #right>
        <button v-if="messages.length > 1" class="restart-btn" @click="restartChat">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
            <path d="M3 3v5h5"/>
          </svg>
        </button>
      </template>
    </AppHeader>

    <!-- 채팅 영역 -->
    <div class="chat-body" ref="chatBody">
      <div class="chat-messages">
        <!-- 메시지 목록 -->
        <template v-for="(msg, idx) in messages" :key="idx">
          <!-- 시스템/봇 메시지 -->
          <div v-if="msg.type === 'bot'" class="message-row bot">
            <div class="message-bubble bot-bubble">
              <p v-html="msg.text"></p>
            </div>
            <!-- 선택 옵션 (마지막 봇 메시지에만) -->
            <div v-if="msg.options && idx === messages.length - 1 && !isTyping" class="options-area">
              <button
                v-for="opt in msg.options"
                :key="opt.value"
                class="option-btn"
                :class="{
                  'has-icon': opt.icon,
                  'selected': isThemeStep && selections.themes.includes(opt.value)
                }"
                @click="selectOption(opt)"
              >
                <span v-if="opt.icon" class="opt-icon">{{ opt.icon }}</span>
                <span class="opt-label">{{ opt.label }}</span>
                <span v-if="opt.count" class="opt-count">{{ opt.count }}개</span>
                <span v-if="isThemeStep && selections.themes.includes(opt.value)" class="opt-check">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                    <path d="M5 13l4 4L19 7"/>
                  </svg>
                </span>
              </button>
              <!-- 테마 스텝: 선택 완료 버튼 -->
              <button v-if="isThemeStep && selections.themes.length > 0" class="confirm-btn" @click="skipStep">
                선택 완료 ({{ selections.themes.length }}개)
              </button>
              <!-- 일반 스킵 버튼 -->
              <button v-if="msg.skippable" class="skip-btn" @click="skipStep">
                {{ isThemeStep ? '상관없어요' : '건너뛰기' }}
              </button>
            </div>
          </div>

          <!-- 사용자 메시지 -->
          <div v-else-if="msg.type === 'user'" class="message-row user">
            <div class="message-bubble user-bubble">
              <p>{{ msg.text }}</p>
            </div>
          </div>

          <!-- 결과 메시지 -->
          <div v-else-if="msg.type === 'result'" class="message-row bot">
            <div class="message-bubble bot-bubble result-bubble">
              <p class="result-title">{{ msg.text }}</p>
              <p class="result-count">총 <strong>{{ msg.count }}</strong>개 현장을 찾았어요</p>
            </div>
            <!-- 결과 카드 -->
            <div class="result-cards">
              <!-- 꾸우챗 광고 카드 (맨 위) -->
              <div
                v-if="msg.chatAd"
                class="result-card ad-card"
                @click="goToAdCampaign(msg.chatAd)"
              >
                <div class="card-thumb">
                  <img v-if="msg.chatAd.imageUrl" :src="msg.chatAd.imageUrl" :alt="msg.chatAd.title" />
                  <div v-else class="thumb-empty">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                      <polyline points="9,22 9,12 15,12 15,22"/>
                    </svg>
                  </div>
                  <span class="card-badge ad-badge">AD</span>
                </div>
                <div class="card-info">
                  <span class="card-region">{{ msg.chatAd.propertyName }}</span>
                  <h4 class="card-name">{{ msg.chatAd.title }}</h4>
                  <p class="card-subtitle">{{ msg.chatAd.subtitle }}</p>
                </div>
              </div>

              <!-- 일반 현장 카드 -->
              <div
                v-for="property in msg.properties"
                :key="property.id"
                class="result-card"
                @click="goToProperty(property.id)"
              >
                <div class="card-thumb">
                  <img v-if="property.thumbnailUrl" :src="property.thumbnailUrl" :alt="property.name" />
                  <div v-else class="thumb-empty">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                      <polyline points="9,22 9,12 15,12 15,22"/>
                    </svg>
                  </div>
                  <span class="card-badge" :class="getStatusClass(property.status)">
                    {{ getStatusLabel(property.status) }}
                  </span>
                </div>
                <div class="card-info">
                  <span class="card-region">{{ property.regionMain }} {{ property.regionSub }}</span>
                  <h4 class="card-name">{{ property.name }}</h4>
                  <div class="card-meta">
                    <span v-if="property.propertyType">{{ getTypeLabel(property.propertyType) }}</span>
                    <span v-if="property.totalUnits">{{ property.totalUnits.toLocaleString() }}세대</span>
                  </div>
                  <div v-if="property.initialFund" class="card-price">
                    초기자금 <strong>{{ property.initialFund }}</strong>
                  </div>
                </div>
              </div>
              <!-- 더보기 -->
              <button v-if="msg.hasMore" class="more-btn" @click="goToList">
                전체 {{ msg.totalCount }}개 보기
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </button>
            </div>
            <!-- 다시 찾기 -->
            <button class="retry-btn" @click="restartChat">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                <path d="M3 3v5h5"/>
              </svg>
              다시 찾기
            </button>
          </div>
        </template>

        <!-- 타이핑 인디케이터 -->
        <div v-if="isTyping" class="message-row bot">
          <div class="typing-bubble">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useProperty } from '@/composables/admin/useProperty'
import { useChatAd } from '@/composables/partners/useChatAd'
import AppHeader from '@/components/common/AppHeader.vue'

export default {
  name: 'ChatView',
  components: { AppHeader },
  setup() {
    const router = useRouter()
    const { fetchProperties, properties } = useProperty()
    const { getRandomAd, incrementClick, incrementImpression } = useChatAd()
    const chatBody = ref(null)

    const isLoading = ref(true)
    const isTyping = ref(false)
    const messages = ref([])
    const currentStep = ref(0)
    const chatAd = ref(null)

    // 사용자 선택
    const selections = ref({
      region: null,
      propertyType: null,
      scale: null,
      themes: []
    })

    // 옵션 데이터
    const typeOptions = [
      { value: 'apartment', label: '아파트' },
      { value: 'officetel', label: '오피스텔' },
      { value: 'villa', label: '빌라' },
      { value: 'commercial', label: '상가' }
    ]

    const statusOptions = [
      { value: 'ongoing', label: '분양중', colorClass: 'green' },
      { value: 'upcoming', label: '분양예정', colorClass: 'amber' },
      { value: 'completed', label: '분양완료', colorClass: 'gray' }
    ]

    const scaleOptions = [
      { value: 'small', label: '소형 (500세대 미만)', max: 500 },
      { value: 'medium', label: '중형 (500~1000세대)', min: 500, max: 1000 },
      { value: 'large', label: '대형 (1000세대 이상)', min: 1000 }
    ]

    // 테마 옵션
    const themeOptions = [
      { value: 'money', label: '소액투자' },
      { value: 'brand', label: '1군 브랜드' },
      { value: 'subway', label: '역세권' },
      { value: 'safe', label: '안심보장제' },
      { value: 'school', label: '학세권' },
      { value: 'forest', label: '숲세권' },
      { value: 'job', label: '직주근접' },
      { value: 'mall', label: '몰세권' }
    ]

    // 필터링
    const filteredProperties = computed(() => {
      let result = [...properties.value]

      if (selections.value.region) {
        result = result.filter(p => p.regionMain === selections.value.region)
      }
      if (selections.value.propertyType) {
        result = result.filter(p => p.propertyType === selections.value.propertyType)
      }
      if (selections.value.scale) {
        const opt = scaleOptions.find(s => s.value === selections.value.scale)
        if (opt) {
          result = result.filter(p => {
            const units = p.totalUnits || 0
            if (opt.min && units < opt.min) return false
            if (opt.max && units >= opt.max) return false
            return true
          })
        }
      }
      if (selections.value.themes.length > 0) {
        result = result.filter(p => {
          if (!p.themes) return false
          return selections.value.themes.some(t => p.themes.includes(t))
        })
      }

      return result
    })

    // 스크롤
    const scrollToBottom = () => {
      nextTick(() => {
        if (chatBody.value) {
          chatBody.value.scrollTo({
            top: chatBody.value.scrollHeight,
            behavior: 'smooth'
          })
        }
      })
    }

    // 봇 메시지 추가
    const addBotMessage = async (text, options = null, skippable = false) => {
      isTyping.value = true
      scrollToBottom()
      await new Promise(r => setTimeout(r, 500 + Math.random() * 300))
      isTyping.value = false
      messages.value.push({ type: 'bot', text, options, skippable })
      scrollToBottom()
    }

    // 사용자 메시지 추가
    const addUserMessage = (text) => {
      messages.value.push({ type: 'user', text })
      scrollToBottom()
    }

    // 결과 메시지 추가
    const addResultMessage = async () => {
      const props = filteredProperties.value
      const displayProps = props.slice(0, 5)

      // 꾸우챗 광고 조회
      try {
        chatAd.value = await getRandomAd()
        if (chatAd.value) {
          await incrementImpression(chatAd.value.id)
        }
      } catch (err) {
        console.error('꾸우챗 광고 조회 실패:', err)
        chatAd.value = null
      }

      messages.value.push({
        type: 'result',
        text: '조건에 맞는 현장이에요!',
        count: props.length,
        totalCount: props.length,
        properties: displayProps,
        hasMore: props.length > 5,
        chatAd: chatAd.value
      })
      scrollToBottom()
    }

    // 가용 옵션 계산
    const getAvailableRegions = () => {
      const counts = {}
      properties.value.forEach(p => {
        if (p.regionMain) {
          counts[p.regionMain] = (counts[p.regionMain] || 0) + 1
        }
      })
      return Object.entries(counts)
        .map(([value, count]) => ({ value, label: value, count }))
        .sort((a, b) => b.count - a.count)
    }

    const getAvailableTypes = () => {
      let base = filteredProperties.value
      const types = new Set(base.map(p => p.propertyType).filter(Boolean))
      return typeOptions.filter(t => types.has(t.value))
    }

    const getAvailableScales = () => {
      let base = filteredProperties.value
      return scaleOptions.filter(opt => {
        return base.some(p => {
          const units = p.totalUnits || 0
          if (opt.min && units < opt.min) return false
          if (opt.max && units >= opt.max) return false
          return true
        })
      })
    }

    const getAvailableThemes = () => {
      let base = filteredProperties.value
      const themes = new Set()
      base.forEach(p => {
        if (p.themes) p.themes.forEach(t => themes.add(t))
      })
      return themeOptions.filter(t => themes.has(t.value))
    }

    // 대화 흐름 (4단계)
    const steps = [
      {
        key: 'region',
        question: '어느 <strong>지역</strong>에서 찾으세요?',
        getOptions: getAvailableRegions,
        skippable: false
      },
      {
        key: 'propertyType',
        question: '어떤 <strong>유형</strong>을 찾으세요?',
        getOptions: getAvailableTypes,
        skippable: true
      },
      {
        key: 'scale',
        question: '선호하는 <strong>단지 규모</strong>는요?',
        getOptions: getAvailableScales,
        skippable: true
      },
      {
        key: 'themes',
        question: '중요하게 생각하는 <strong>조건</strong>이 있나요?',
        getOptions: getAvailableThemes,
        skippable: true,
        multi: true
      }
    ]

    // 다음 스텝으로
    const proceedToNextStep = async () => {
      // 남은 현장 수 체크
      if (filteredProperties.value.length === 0) {
        await addBotMessage('조건에 맞는 현장이 없네요. 다시 찾아볼까요?')
        return
      }

      // 모든 스텝(4개)을 다 거쳤으면 결과 표시
      if (currentStep.value >= steps.length) {
        await addBotMessage('잠깐만요, 찾아볼게요...')
        await new Promise(r => setTimeout(r, 800))
        await addResultMessage()
        return
      }

      const step = steps[currentStep.value]
      const options = step.getOptions()

      if (options.length === 0) {
        // 옵션이 없으면 자동 스킵하고 다음으로
        currentStep.value++
        await proceedToNextStep()
        return
      }

      await addBotMessage(step.question, options, step.skippable)
    }

    // 옵션 선택
    const selectOption = async (opt) => {
      const step = steps[currentStep.value]
      if (!step) return // 스텝이 없으면 무시

      if (step.multi) {
        // 다중 선택 (테마) - 새 배열로 교체하여 반응성 보장
        const currentThemes = [...selections.value.themes]
        const idx = currentThemes.indexOf(opt.value)
        if (idx === -1) {
          currentThemes.push(opt.value)
        } else {
          currentThemes.splice(idx, 1)
        }
        selections.value = { ...selections.value, themes: currentThemes }
        return
      }

      // 단일 선택
      addUserMessage(opt.label)
      selections.value[step.key] = opt.value
      currentStep.value++
      await proceedToNextStep()
    }

    // 스킵
    const skipStep = async () => {
      const step = steps[currentStep.value]

      if (step.multi && selections.value.themes.length > 0) {
        // 테마 선택 완료
        const labels = selections.value.themes.map(t =>
          themeOptions.find(o => o.value === t)?.label
        ).join(', ')
        addUserMessage(labels)
      } else {
        addUserMessage('상관없어요')
      }

      currentStep.value++
      await proceedToNextStep()
    }

    // 재시작
    const restartChat = async () => {
      messages.value = []
      currentStep.value = 0
      selections.value = {
        region: null,
        propertyType: null,
        scale: null,
        themes: []
      }
      await startChat()
    }

    // 시작
    const startChat = async () => {
      await addBotMessage('안녕하세요! 원하는 분양 현장을 찾아드릴게요.')
      await proceedToNextStep()
    }

    // 네비게이션
    const goToProperty = (id) => router.push(`/properties/${id}`)
    const goToList = () => {
      const params = new URLSearchParams()
      if (selections.value.region) params.set('region', selections.value.region)
      if (selections.value.propertyType) params.set('type', selections.value.propertyType)
      router.push(`/properties?${params.toString()}`)
    }

    // 광고 클릭
    const goToAdCampaign = async (ad) => {
      if (ad && ad.id) {
        await incrementClick(ad.id)
      }
      if (ad && ad.linkUrl) {
        // URL에 이미 쿼리 파라미터가 있으면 &, 없으면 ? 사용
        const separator = ad.linkUrl.includes('?') ? '&' : '?'
        router.push(`${ad.linkUrl}${separator}from=chatad`)
      }
    }

    // 헬퍼
    const getTypeLabel = (t) => typeOptions.find(o => o.value === t)?.label || t
    const getStatusLabel = (s) => statusOptions.find(o => o.value === s)?.label || s
    const getStatusClass = (s) => statusOptions.find(o => o.value === s)?.colorClass || 'gray'

    // 현재 테마 스텝인지 확인
    const isThemeStep = computed(() => {
      const step = steps[currentStep.value]
      return step?.multi === true
    })

    // 초기화
    onMounted(async () => {
      await fetchProperties()
      isLoading.value = false
      await startChat()
    })

    return {
      chatBody,
      isLoading,
      isTyping,
      messages,
      selections,
      isThemeStep,
      selectOption,
      skipStep,
      restartChat,
      goToProperty,
      goToList,
      goToAdCampaign,
      getTypeLabel,
      getStatusLabel,
      getStatusClass
    }
  }
}
</script>

<style scoped>
.chat-page {
  min-height: 100vh;
  min-height: 100dvh;
  background: #ffffff;
  display: flex;
  flex-direction: column;
}

/* 헤더 스티키 */
.chat-page :deep(header) {
  position: sticky;
  top: 0;
  z-index: 100;
}

/* 재시작 버튼 (헤더) */
.restart-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  border-radius: 20px;
  -webkit-tap-highlight-color: transparent;
}

.restart-btn:active {
  background: #f1f5f9;
}

/* 채팅 영역 */
.chat-body {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.chat-messages {
  padding: 16px;
  padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
  max-width: 500px;
  margin: 0 auto;
}

/* 메시지 행 */
.message-row {
  margin-bottom: 12px;
}

.message-row.user {
  display: flex;
  justify-content: flex-end;
}

/* 메시지 버블 */
.message-bubble {
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 20px;
  font-size: 15px;
  line-height: 1.5;
  word-break: keep-all;
}

.message-bubble p {
  margin: 0;
}

.bot-bubble {
  background: #ffffff;
  color: #333;
  border-radius: 4px 20px 20px 20px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
}

.user-bubble {
  background: #3b82f6;
  color: #fff;
  border-radius: 20px 20px 4px 20px;
}

/* 옵션 영역 */
.options-area {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  max-width: 100%;
}

.option-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: #ffffff;
  border: 1.5px solid #e0e0e0;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition: all 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.option-btn:active {
  transform: scale(0.96);
  background: #f0f7ff;
  border-color: #3b82f6;
}

.option-btn.selected {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #2563eb;
}

.option-btn.has-icon {
  padding-left: 12px;
}

.opt-check {
  display: flex;
  align-items: center;
  color: #2563eb;
}

.opt-icon {
  font-size: 16px;
}

.opt-count {
  font-size: 12px;
  color: #999;
  margin-left: 2px;
}

.skip-btn {
  padding: 10px 16px;
  background: transparent;
  border: 1.5px dashed #ccc;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  color: #888;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.skip-btn:active {
  background: #f5f5f5;
}

/* 선택 완료 버튼 */
.confirm-btn {
  padding: 10px 20px;
  background: #2563eb;
  border: none;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.confirm-btn:active {
  background: #1d4ed8;
}

/* 타이핑 인디케이터 */
.typing-bubble {
  display: inline-flex;
  gap: 4px;
  padding: 14px 18px;
  background: #ffffff;
  border-radius: 4px 20px 20px 20px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
}

.typing-bubble span {
  width: 8px;
  height: 8px;
  background: #bbb;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out;
}

.typing-bubble span:nth-child(1) { animation-delay: 0s; }
.typing-bubble span:nth-child(2) { animation-delay: 0.2s; }
.typing-bubble span:nth-child(3) { animation-delay: 0.4s; }

@keyframes bounce {
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-8px);
  }
}

/* 결과 버블 */
.result-bubble {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: #fff;
}

.result-title {
  font-weight: 600;
  margin-bottom: 4px !important;
}

.result-count strong {
  font-weight: 700;
}

/* 결과 카드 */
.result-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
}

.result-card {
  display: flex;
  gap: 12px;
  padding: 12px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  cursor: pointer;
  transition: transform 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.result-card:active {
  transform: scale(0.98);
}

.card-thumb {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
}

.card-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb-empty {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bbb;
}

.card-badge {
  position: absolute;
  top: 4px;
  left: 4px;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 600;
  border-radius: 4px;
}

.card-badge.green {
  background: rgba(34, 197, 94, 0.95);
  color: #fff;
}

.card-badge.amber {
  background: rgba(245, 158, 11, 0.95);
  color: #fff;
}

.card-badge.gray {
  background: rgba(100, 116, 139, 0.95);
  color: #fff;
}

/* 광고 배지 */
.ad-badge {
  background: rgba(16, 185, 129, 0.95);
  color: #fff;
}

/* 광고 카드 스타일 */
.ad-card {
  background: #ecfdf5;
  border: 2px solid #10b981;
}

.ad-card .card-region {
  color: #059669;
}

.ad-card .card-name {
  color: #047857;
}

.card-subtitle {
  font-size: 12px;
  color: #666;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.card-region {
  font-size: 12px;
  color: #888;
}

.card-name {
  font-size: 14px;
  font-weight: 600;
  color: #111;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-meta {
  font-size: 12px;
  color: #666;
  display: flex;
  gap: 6px;
}

.card-price {
  font-size: 13px;
  color: #666;
  margin-top: 2px;
}

.card-price strong {
  color: #3b82f6;
  font-weight: 600;
}

/* 더보기 버튼 */
.more-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  padding: 14px;
  background: #f8f8f8;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #3b82f6;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.more-btn:active {
  background: #f0f0f0;
}

/* 다시 찾기 버튼 (결과 화면) */
.retry-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 16px;
  padding: 12px 20px;
  background: #ffffff;
  border: 1.5px solid #e0e0e0;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.retry-btn:active {
  background: #f5f5f5;
}

/* 반응형 */
@media (min-width: 640px) {
  .chat-messages {
    max-width: 480px;
  }

  .card-thumb {
    width: 100px;
    height: 100px;
  }
}
</style>
